<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  NAlert,
  NButton,
  NForm,
  NFormItem,
  NInput,
  NInputNumber,
  NModal,
  NProgress,
  NRadio,
  NRadioGroup,
  NSpace,
  NSpin,
  useMessage,
} from 'naive-ui'
import AppSelectField from '@/components/AppSelectField.vue'
import { useWorkspaceStore } from '@/store/workspace'
import { useAuthStore } from '@/features/auth'
import { usePublishPanel } from '@/publish/composables/usePublishPanel'
import { getPublishToolData } from '@/publish/api'
import { buildPublishedGamePageUrl } from '@/publish/gamePageUrl'
import { getAppDomainPreset, normalizeAppRuntimeEnv } from '@/config/domainPresets'
import { truncateProjectName } from '@/utils/projectNameLimit'
import type { PublishPayType, PublishRawTagList } from '@/publish/types'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  'update:show': [boolean]
}>()

const message = useMessage()
const auth = useAuthStore()
const workspace = useWorkspaceStore()
const {
  loadingContext,
  runningChecks,
  publishing,
  publishLocked,
  context,
  result,
  form,
  checks,
  progressItems,
  uploadMessage,
  tagWebOpen,
  openAfterTagSetup,
  initializeContext,
  runChecksAction,
  syncFlowerUnlockFromProject,
  publishAction,
  saveTagSelection,
  clearTagSelection,
  openWithTagGuard,
} = usePublishPanel()

type SelectOption = {
  label: string
  value: number
}

const emptySelectOption: SelectOption = { label: '无', value: -1000 }
const POST_PUBLISH_STEP_KEYS = new Set(['update-log', 'post-save', 'game-attrs', 'tag-submit', 'cover-upload'])
const activityOptions = ref<SelectOption[]>([emptySelectOption])
const copyrightOwnerOptions = ref<SelectOption[]>([emptySelectOption])
const copyrightWorkOptions = ref<SelectOption[]>([emptySelectOption])
const loadingDetails = ref(false)
const tagWebError = ref('')
const payTypeConfirmOpen = ref(false)
const pendingPayType = ref<PublishPayType | null>(null)
const isSyncingRemoteDetails = ref(false)
const detailsAllDisabled = ref(false)
const detailsActionDisabled = ref(false)
const detailsActivityDisabled = ref(false)
const appEnv = normalizeAppRuntimeEnv(import.meta.env.MODE || import.meta.env.VITE_APP_ENV)
const tagWebsite = getAppDomainPreset(appEnv).website.replace(/\/+$/g, '')
const shouldLockPayTypeDowngrade = computed(() =>
  Boolean(result.value) || context.value?.isFirstPublish === false,
)

const payTypeOptions = computed(() => {
  const currentType = form.payType
  return [
    { label: '免费', value: 0, disabled: shouldLockPayTypeDowngrade.value && (currentType === 1 || currentType === 3) },
    { label: '鲜花锁', value: 1, disabled: shouldLockPayTypeDowngrade.value && currentType === 3 },
    { label: '剧情锁', value: 3, disabled: false },
  ]
})
const pendingPayTypeMessage = computed(() => {
  if (pendingPayType.value === 1) {
    return '收费方式调整为【鲜花锁】后，发布成功后不可再改为【免费】，是否确认修改？'
  }
  if (pendingPayType.value === 3) {
    return '收费方式调整为【剧情锁】后，发布成功后不可再改为【鲜花锁】、【免费】，是否确认修改？'
  }
  return ''
})

const displayProgressItems = computed(() =>
  progressItems.value.filter((item) => item.key !== 'request-log'),
)
const mainProgressItems = computed(() =>
  displayProgressItems.value.filter((item) => !POST_PUBLISH_STEP_KEYS.has(item.key)),
)
const postPublishItems = computed(() =>
  displayProgressItems.value.filter((item) => POST_PUBLISH_STEP_KEYS.has(item.key) && item.status !== 'pending'),
)

const publishStageItems = computed(() =>
  displayProgressItems.value.filter((item) => !['context', 'checks'].includes(item.key)),
)
const isPublishAccepted = computed(() =>
  Boolean(result.value) || mainProgressItems.value.some((item) => item.key === 'publish' && item.status === 'done'),
)

const showPublishProgress = computed(() =>
  publishStageItems.value.some((item) => item.status !== 'pending')
  || Boolean(result.value)
  || publishing.value,
)

const publishProgressPercent = computed(() => {
  if (isPublishAccepted.value) return 100
  const total = mainProgressItems.value.length || 1
  const doneCount = mainProgressItems.value.filter((item) => item.status === 'done').length
  const runningCount = mainProgressItems.value.filter((item) => item.status === 'running').length
  return Math.max(0, Math.min(100, Math.round(((doneCount + (runningCount > 0 ? 0.5 : 0)) / total) * 100)))
})

const publishProgressStatus = computed(() => {
  if (isPublishAccepted.value) return 'success'
  if (mainProgressItems.value.some((item) => item.status === 'error')) return 'error'
  return 'default'
})

const mainErrorItem = computed(() =>
  [...mainProgressItems.value].reverse().find((item) => item.status === 'error'),
)

const publishProgressTitle = computed(() => {
  if (result.value) return result.value.message || '发布成功'
  if (mainErrorItem.value) return `发布失败：${mainErrorItem.value.label}`
  if (isPublishAccepted.value) {
    const runningPostItem = postPublishItems.value.find((item) => item.status === 'running')
    if (runningPostItem) return `发布成功，${runningPostItem.detail || runningPostItem.label}`
    const errorPostItem = postPublishItems.value.find((item) => item.status === 'error')
    if (errorPostItem) return `发布成功，${errorPostItem.label}未完成`
    return '发布成功'
  }
  const runningItem = mainProgressItems.value.find((item) => item.status === 'running')
  if (runningItem) return runningItem.detail || runningItem.label
  const lastActiveItem = [...mainProgressItems.value].reverse().find((item) => item.status !== 'pending')
  return lastActiveItem?.detail || lastActiveItem?.label || ''
})

const publishProgressDetail = computed(() => {
  if (mainErrorItem.value?.detail) return mainErrorItem.value.detail
  const runningItem = mainProgressItems.value.find((item) => item.status === 'running')
  if (runningItem?.detail && runningItem.detail !== publishProgressTitle.value) return runningItem.detail
  return uploadMessage.value
})

const visibleChecks = computed(() => checks.value.filter((item) => item.level !== 'info'))
const isFirstPublish = computed(() => context.value?.isFirstPublish ?? true)
const serverGameVersionText = computed(() => {
  const version = Number(context.value?.serverCurrentVersion || 0)
  return Number.isFinite(version) && version > 0 ? String(Math.floor(version)) : '未发布'
})
const publishedGamePageUrl = computed(() => buildPublishedGamePageUrl(context.value?.gindex ?? '', appEnv))
const isFinishedRadioValue = computed({
  get: () => (form.isFinished ? 1 : 0),
  set: (value: number) => {
    form.isFinished = value === 1
  },
})
const tagWebProjectName = computed(() => form.projectName.trim() || workspace.project?.storyTree.ProjectName?.trim() || '当前工程')
const tagWebUrl = computed(() => {
  const url = new URL('/tag/tool_tag', tagWebsite)
  url.searchParams.set('mark', 'web_tool')
  url.searchParams.set('embed', '1')
  url.searchParams.set('guid', context.value?.guid || '')
  url.searchParams.set('gindex', context.value?.gindex || '')
  url.searchParams.set('version', '1')
  url.searchParams.set('role_customized', 'false')
  return `${url.toString()}&project_name=${encodeLegacyProjectName(tagWebProjectName.value)}`
})

function encodeLegacyProjectName(value: string): string {
  return Array.from(value).map((char) => {
    const code = char.charCodeAt(0)
    if (/^[A-Za-z0-9@*_+\-./]$/.test(char)) return char
    if (code < 256) return `%${code.toString(16).toUpperCase().padStart(2, '0')}`
    return `%u${code.toString(16).toUpperCase().padStart(4, '0')}`
  }).join('')
}

function handleProjectNameUpdate(value: string) {
  form.projectName = truncateProjectName(value)
}

function handleFinishedUpdate(value: number) {
  if (detailsAllDisabled.value) return
  if (isFirstPublish.value && value === 1) return
  isFinishedRadioValue.value = value
}

watch(isFirstPublish, (first) => {
  if (first) form.isFinished = false
})

function handleCopyrightActionUpdate(value: '0' | 'get_partner_list' | 'star_copyrigt_company_list') {
  if (detailsAllDisabled.value || detailsActionDisabled.value) return
  form.copyrightAction = value
}

function handlePayTypeUpdate(value: number) {
  if (value !== 0 && value !== 1 && value !== 3) return
  if (value === form.payType) return
  if (value === 1 || value === 3) {
    pendingPayType.value = value
    payTypeConfirmOpen.value = true
    return
  }
  form.payType = value
  form.flowerUnlock = 0
}

function confirmPayTypeUpdate() {
  if (pendingPayType.value === null) return
  form.payType = pendingPayType.value
  if (form.payType === 1) {
    syncFlowerUnlockFromProject(form.payType)
  } else {
    form.flowerUnlock = 0
  }
  pendingPayType.value = null
  payTypeConfirmOpen.value = false
}

function cancelPayTypeUpdate() {
  pendingPayType.value = null
  payTypeConfirmOpen.value = false
}

function resetUnpublishedPayTypeDraft() {
  if (context.value?.gindex || result.value) return
  form.payType = 0
  form.flowerUnlock = 0
  form.hpNumber = 0
  pendingPayType.value = null
  payTypeConfirmOpen.value = false
}

function isAllowedTagOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname
    return host === '66rpg.com' || host.endsWith('.66rpg.com')
  } catch {
    return false
  }
}

function parseTagWebMessage(data: unknown): Record<string, unknown> | null {
  if (typeof data === 'string') {
    if (data.includes('closeTag')) return { action: 'closeTag' }
    try {
      return JSON.parse(data) as Record<string, unknown>
    } catch {
      return null
    }
  }
  return data && typeof data === 'object' ? data as Record<string, unknown> : null
}

function extractArrayPayload(
  value: unknown,
  keys: string[] = ['data', 'list', 'tag_list', 'company_list', 'star_list'],
): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value as Record<string, unknown>[]
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    for (const key of keys) {
      if (Array.isArray(record[key])) {
        return record[key] as Record<string, unknown>[]
      }
    }
    for (const nested of Object.values(record)) {
      const nestedResult = extractArrayPayload(nested, keys)
      if (nestedResult.length) return nestedResult
    }
  }
  return []
}

function pickFirstText(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

function extractRemoteSynopsis(gameAttr: Record<string, unknown>): string {
  const direct = pickFirstText(gameAttr, [
    'synopsis',
    'intro',
    'short_intro',
    'game_intro',
    'game_desc',
    'description',
    'summary',
  ])
  if (direct) return direct
  const gameInfo = gameAttr.game_info as Record<string, unknown> | undefined
  return gameInfo
    ? pickFirstText(gameInfo, ['synopsis', 'intro', 'short_intro', 'game_intro', 'game_desc', 'description', 'summary'])
    : ''
}

function pickNumberByPath(record: Record<string, unknown>, path: string): number | null {
  let current: unknown = record
  for (const key of path.split('.')) {
    if (!current || typeof current !== 'object') return null
    current = (current as Record<string, unknown>)[key]
  }
  const value = Number(current)
  return Number.isFinite(value) ? value : null
}

function pickFirstNumber(record: Record<string, unknown>, paths: string[]): number | null {
  for (const path of paths) {
    const value = pickNumberByPath(record, path)
    if (value !== null) return value
  }
  return null
}

function extractObjectPayload(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const record = value as Record<string, unknown>
  if (record.data && typeof record.data === 'object' && !Array.isArray(record.data)) {
    return extractObjectPayload(record.data)
  }
  return record
}

function findSelectValue(id: number | null, name: string): number {
  if (id !== null && id > 0 && copyrightOwnerOptions.value.some((item) => item.value === id)) return id
  return copyrightOwnerOptions.value.find((item) => item.label === name)?.value ?? -1000
}

function findWorkSelectValue(id: number | null, name: string): number {
  if (id !== null && id > 0 && copyrightWorkOptions.value.some((item) => item.value === id)) return id
  return copyrightWorkOptions.value.find((item) => item.label === name)?.value ?? -1000
}

function syncRemotePublishForm(gameAttr: Record<string, unknown>): void {
  const activeTid = pickFirstNumber(gameAttr, [
    'active_tag.active_tid',
    'activity_tid',
    'active_tid',
    'activity.tid',
  ])
  if (activeTid !== null) {
    form.activityTid = activeTid > 0 ? activeTid : -1000
  }

  const hpNumber = pickFirstNumber(gameAttr, [
    'hp_number',
    'hpNumber',
    'game_info.hp_number',
    'game_info.hpNumber',
  ])
  if (hpNumber !== null) {
    form.hpNumber = Math.max(0, Math.floor(hpNumber))
  }

  const completeStatus = pickFirstNumber(gameAttr, [
    'complete_status',
    'completeStatus',
    'is_finished',
    'isFinished',
    'game_info.complete_status',
    'game_info.completeStatus',
  ])
  if (completeStatus !== null) {
    form.isFinished = completeStatus !== 0
  }
}

function syncRemoteDetailRestrictions(
  gameAttr: Record<string, unknown>,
  copyrightChoice: Record<string, unknown>,
  gameStatus: Record<string, unknown>,
) {
  detailsAllDisabled.value = false
  detailsActionDisabled.value = false
  detailsActivityDisabled.value = false
  const completeStatus = pickFirstNumber(gameAttr, [
    'complete_status',
    'completeStatus',
    'is_finished',
    'isFinished',
    'game_info.complete_status',
    'game_info.completeStatus',
  ]) ?? 0
  const copyrightType = pickFirstNumber(copyrightChoice, ['type', 'copyright_type', 'copyrightType']) ?? 1
  const checkLevel = pickFirstNumber(gameStatus, ['check_level', 'checkLevel'])
  const authorLevel = pickFirstNumber(gameAttr, ['author_level', 'authorLevel', 'game_info.author_level', 'game_info.authorLevel']) ?? 0
  const applyCompleteFlag = pickFirstNumber(gameAttr, [
    'apply_complete_flag',
    'applyCompleteFlag',
    'game_info.apply_complete_flag',
    'game_info.applyCompleteFlag',
  ]) ?? 0
  const isContractAuthor = authorLevel >= 2 && applyCompleteFlag === 1
  if (completeStatus === 0) {
    detailsActionDisabled.value = copyrightType !== 1
    detailsActivityDisabled.value = checkLevel === 2 || checkLevel === 3 || checkLevel === -6
    form.canSyncGameAttrs = true
    return
  }
  if (completeStatus === 1) {
    detailsAllDisabled.value = true
    detailsActivityDisabled.value = true
    form.canSyncGameAttrs = false
    return
  }
  if (completeStatus === 2) {
    if (isContractAuthor) {
      detailsActionDisabled.value = true
      form.canSyncGameAttrs = true
    } else {
      detailsAllDisabled.value = true
      form.canSyncGameAttrs = false
    }
    detailsActivityDisabled.value = true
    return
  }
  form.canSyncGameAttrs = !detailsAllDisabled.value
}

async function loadActivityOptions() {
  const data = await getPublishToolData({ action: 'get_active_tags', uid: auth.uid })
  const payload = extractArrayPayload(data)
  activityOptions.value = [
    emptySelectOption,
    ...payload.map((item) => ({
      label: String((item as Record<string, unknown>).tname ?? ''),
      value: Number((item as Record<string, unknown>).tid ?? -1000),
    })),
  ]
}

async function loadCopyrightOwnerOptions(resetSelection = true) {
  if (resetSelection) {
    form.copyrightOwnerId = -1000
    form.copyrightWorkId = -1000
    copyrightWorkOptions.value = [emptySelectOption]
  }
  if (form.copyrightAction === '0') {
    copyrightOwnerOptions.value = [emptySelectOption]
    return
  }
  if (form.copyrightAction === 'get_partner_list') {
    const data = await getPublishToolData({ action: 'get_partner_list', uid: auth.uid, channel_id: 0 })
    const payload = extractArrayPayload(data)
    copyrightOwnerOptions.value = [
      emptySelectOption,
      ...payload.map((item) => ({
        label: String((item as Record<string, unknown>).name ?? ''),
        value: Number((item as Record<string, unknown>).partnerid ?? -1000),
      })),
    ]
    return
  }
  const data = await getPublishToolData({ action: 'star_copyrigt_company_list', uid: auth.uid })
  const payload = extractArrayPayload(data, ['company_list', 'data', 'list'])
  copyrightOwnerOptions.value = [
    emptySelectOption,
    ...payload.map((item) => ({
      label: String(item.company_name ?? ''),
      value: Number(item.company_id ?? -1000),
    })),
  ]
}

async function loadCopyrightWorkOptions(resetSelection = true) {
  if (resetSelection) {
    form.copyrightWorkId = -1000
  }
  if (form.copyrightAction === '0' || form.copyrightOwnerId === -1000) {
    copyrightWorkOptions.value = [emptySelectOption]
    return
  }
  if (form.copyrightAction === 'get_partner_list') {
    const data = await getPublishToolData({
      action: 'get_book_list',
      uid: auth.uid,
      partnerid: form.copyrightOwnerId,
    })
    const payload = extractArrayPayload(data)
    copyrightWorkOptions.value = [
      emptySelectOption,
      ...payload.map((item) => ({
        label: String((item as Record<string, unknown>).name ?? ''),
        value: Number((item as Record<string, unknown>).bookid ?? -1000),
      })),
    ]
    return
  }
  const data = await getPublishToolData({
    action: 'star_copyrigt_star_list',
    uid: auth.uid,
    company_id: form.copyrightOwnerId,
  })
  const payload = extractArrayPayload(data, ['star_list', 'data', 'list'])
  copyrightWorkOptions.value = [
    emptySelectOption,
    ...payload.map((item) => ({
      label: String(item.star_name ?? ''),
      value: Number(item.star_id ?? -1000),
    })),
  ]
}

async function syncRemoteCopyrightChoice(copyrightChoice: Record<string, unknown>) {
  const copyrightType = pickFirstNumber(copyrightChoice, ['type', 'copyright_type', 'copyrightType']) ?? 1
  form.copyrightAction =
    copyrightType === 2
      ? 'get_partner_list'
      : copyrightType === 3
        ? 'star_copyrigt_company_list'
        : '0'
  await loadCopyrightOwnerOptions(false)
  if (form.copyrightAction === '0') {
    form.copyrightOwnerId = -1000
    form.copyrightWorkId = -1000
    copyrightWorkOptions.value = [emptySelectOption]
    return
  }

  if (form.copyrightAction === 'get_partner_list') {
    const bookInfo = (copyrightChoice.book_info as Record<string, unknown> | undefined) ?? {}
    const partnerId = pickFirstNumber(copyrightChoice, ['partner_id', 'partnerid', 'book_info.partner_id', 'book_info.partnerid'])
    const partnerName = String(bookInfo.partner_name ?? copyrightChoice.partner_name ?? '')
    form.copyrightOwnerId = findSelectValue(partnerId, partnerName)
    await loadCopyrightWorkOptions(false)
    const bookId = pickFirstNumber(copyrightChoice, ['book_id', 'bookid', 'book_info.book_id', 'book_info.bookid'])
    const bookName = String(bookInfo.name ?? bookInfo.book_name ?? copyrightChoice.book_name ?? '')
    form.copyrightWorkId = findWorkSelectValue(bookId, bookName)
    return
  }

  if (form.copyrightAction === 'star_copyrigt_company_list') {
    const starInfo = (copyrightChoice.star_info as Record<string, unknown> | undefined) ?? {}
    const companyId = pickFirstNumber(copyrightChoice, ['company_id', 'star_info.company_id'])
    const companyName = String(starInfo.company_name ?? copyrightChoice.company_name ?? '')
    form.copyrightOwnerId = findSelectValue(companyId, companyName)
    await loadCopyrightWorkOptions(false)
    const starId = pickFirstNumber(copyrightChoice, ['star_id', 'star_info.star_id'])
    const starName = String(starInfo.star_name ?? copyrightChoice.star_name ?? '')
    form.copyrightWorkId = findWorkSelectValue(starId, starName)
  }
}

async function loadPublishDetailsState() {
  const project = workspace.project
  form.screenType = project && project.screenHash.width < project.screenHash.height ? 1 : 0
  form.canSyncGameAttrs = true
  if (!auth.uid) return
  const activeContext = context.value
  if (!activeContext) return
  loadingDetails.value = true
  try {
    await loadActivityOptions()
    if (!activeContext.gindex) {
      resetUnpublishedPayTypeDraft()
      syncFlowerUnlockFromProject(form.payType)
      copyrightOwnerOptions.value = [emptySelectOption]
      copyrightWorkOptions.value = [emptySelectOption]
      detailsAllDisabled.value = false
      detailsActionDisabled.value = false
      detailsActivityDisabled.value = false
      form.isFinished = false
      return
    }
    const [copyrightChoiceRes, gameAttrRes, gameStatusRes] = await Promise.all([
      getPublishToolData({
        action: 'get_copyright_choice',
        uid: auth.uid,
        gindex: activeContext.gindex,
      }),
      getPublishToolData({
        action: 'get_game_attr',
        uid: auth.uid,
        gindex: activeContext.gindex,
      }),
      getPublishToolData({
        action: 'game_status',
        uid: auth.uid,
        gindex: activeContext.gindex,
      }),
    ])

    isSyncingRemoteDetails.value = true
    syncFlowerUnlockFromProject(form.payType)
    const copyrightChoice = extractObjectPayload(copyrightChoiceRes)
    const gameAttr = extractObjectPayload(gameAttrRes)
    const gameStatus = extractObjectPayload(gameStatusRes)

    syncRemotePublishForm(gameAttr)
    syncRemoteDetailRestrictions(gameAttr, copyrightChoice, gameStatus)
    const remoteSynopsis = extractRemoteSynopsis(gameAttr)
    if (remoteSynopsis && !form.synopsis.trim()) {
      form.synopsis = remoteSynopsis
    }

    await syncRemoteCopyrightChoice(copyrightChoice)
  } finally {
    isSyncingRemoteDetails.value = false
    loadingDetails.value = false
  }
}

function close() {
  if (publishLocked.value) return
  emit('update:show', false)
}

async function requestOpen() {
  try {
    const mode = await openWithTagGuard()
    if (mode === 'panel') {
      emit('update:show', true)
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : '加载发布上下文失败')
  }
}

async function handlePublish() {
  try {
    await publishAction()
  } catch (error) {
    message.error(error instanceof Error ? error.message : '发布失败')
  }
}

async function handleTagSetupComplete(selection: PublishRawTagList) {
  try {
    await saveTagSelection(selection)
    if (openAfterTagSetup.value) {
      openAfterTagSetup.value = false
      emit('update:show', true)
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : '保存标签设置失败')
  }
}

async function handleTagSetupCancel() {
  try {
    tagWebOpen.value = false
    await clearTagSelection()
    openAfterTagSetup.value = false
  } catch (error) {
    message.error(error instanceof Error ? error.message : '清空标签设置失败')
  }
}

function handleTagWebClose() {
  tagWebError.value = ''
  tagWebOpen.value = false
  void handleTagSetupCancel()
}

function handleTagWebMessage(event: MessageEvent) {
  if (!isAllowedTagOrigin(event.origin)) return
  const data = parseTagWebMessage(event.data)
  const action = String(data?.action ?? '').toLowerCase()
  if (!action) return
  if (action.includes('close')) {
    handleTagWebClose()
    return
  }
  if (action === 'submittag') {
    const tagList = data?.tagList
    if (!tagList || typeof tagList !== 'object') {
      tagWebError.value = '标签页返回数据缺少 tagList'
      return
    }
    tagWebError.value = ''
    tagWebOpen.value = false
    void handleTagSetupComplete(tagList as PublishRawTagList)
  }
}

onMounted(() => {
  window.addEventListener('message', handleTagWebMessage)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handleTagWebMessage)
})

watch(
  () => props.show,
  (show) => {
    if (show) {
      void (async () => {
        try {
          await initializeContext()
          await loadPublishDetailsState()
        } catch (error) {
          message.error(error instanceof Error ? error.message : '加载发布上下文失败')
        }
      })()
      return
    }
    openAfterTagSetup.value = false
    resetUnpublishedPayTypeDraft()
  },
)

watch(
  () => form.copyrightAction,
  () => {
    if (isSyncingRemoteDetails.value) return
    void loadCopyrightOwnerOptions().catch((error) => {
      message.error(error instanceof Error ? error.message : '加载版权方失败')
    })
  },
)

watch(
  () => form.copyrightOwnerId,
  () => {
    if (isSyncingRemoteDetails.value) return
    void loadCopyrightWorkOptions().catch((error) => {
      message.error(error instanceof Error ? error.message : '加载书名失败')
    })
  },
)

defineExpose<{
  requestOpen: () => Promise<void>
}>({
  requestOpen,
})
</script>

<template>
  <Teleport to="body">
    <div v-if="tagWebOpen" class="publish-tag-web-frame-wrap">
      <n-alert v-if="tagWebError" type="error" :show-icon="false" class="publish-tag-web-error">
        {{ tagWebError }}
      </n-alert>
      <iframe
        class="publish-tag-web-frame"
        :src="tagWebUrl"
        title="设置作品标签"
      />
    </div>
  </Teleport>

  <div v-if="show" class="publish-panel-overlay">
    <section class="publish-panel">
      <header class="publish-header">
        <div>
          <div class="publish-title">发布作品</div>
          <div class="publish-subtitle">
            <span>{{ workspace.displayTitle || '当前工程' }}</span>
            <span class="publish-subtitle-version">线上版本：{{ serverGameVersionText }}</span>
          </div>
        </div>
        <n-space class="publish-header-actions" :wrap="true" align="center">
          <a
            v-if="publishedGamePageUrl"
            class="publish-result-link publish-header-game-link"
            :href="publishedGamePageUrl"
            target="_blank"
            rel="noreferrer"
          >
            打开作品页面
          </a>
          <n-button
            size="small"
            type="primary"
            :loading="publishing || runningChecks || loadingContext"
            :disabled="publishLocked || runningChecks || loadingContext || loadingDetails"
            @click="handlePublish"
          >
            开始发布
          </n-button>
          <n-button size="small" :disabled="publishLocked" @click="close">关闭</n-button>
        </n-space>
      </header>

      <section v-if="showPublishProgress" class="publish-progress-card">
        <div class="publish-progress-head">
          <div
            class="publish-progress-title"
            :class="{ 'publish-progress-title--result': result }"
          >
            {{ publishProgressTitle || '准备发布' }}
          </div>
          <div class="publish-progress-percent">{{ publishProgressPercent }}%</div>
        </div>
        <n-progress
          type="line"
          :percentage="publishProgressPercent"
          :status="publishProgressStatus"
          :show-indicator="false"
        />
        <div v-if="publishProgressDetail" class="publish-progress-detail">
          {{ publishProgressDetail }}
        </div>
        <div v-if="postPublishItems.length" class="publish-post-sync-list">
          <div
            v-for="item in postPublishItems"
            :key="item.key"
            class="publish-post-sync-item"
            :class="`publish-post-sync-item--${item.status}`"
          >
            <span class="publish-post-sync-dot" />
            <span class="publish-post-sync-label">{{ item.label }}</span>
            <span class="publish-post-sync-detail">{{ item.detail }}</span>
          </div>
        </div>
      </section>

      <section v-if="visibleChecks.length" class="publish-check-card">
        <n-alert
          v-for="item in visibleChecks"
          :key="item.key"
          :type="item.level === 'error' ? 'error' : 'warning'"
          :bordered="false"
          class="publish-check-alert"
        >
          <strong>{{ item.title }}</strong>
          <div class="publish-check-message">{{ item.message }}</div>
        </n-alert>
      </section>

      <div class="publish-grid">
        <section class="publish-main-card">
          <n-spin :show="loadingDetails || runningChecks" description="正在同步线上信息">
            <n-form label-placement="left" label-width="92" :disabled="loadingDetails || runningChecks">
              <n-form-item label="作品名称">
                <div class="publish-project-name-field">
                  <n-input
                    :value="form.projectName"
                    placeholder="请输入作品名称"
                    @update:value="handleProjectNameUpdate"
                  />
                  <div class="publish-field-hint">不超过15个汉字或30个英文</div>
                </div>
              </n-form-item>
              <n-form-item v-if="isFirstPublish" label="作品简介">
                <n-input
                  v-model:value="form.synopsis"
                  type="textarea"
                  placeholder="首次发布时作为主站作品简介展示，建议控制在 200 字以内"
                  :autosize="{ minRows: 4, maxRows: 6 }"
                />
              </n-form-item>
              <n-form-item
                v-else
                label="更新日志"
              >
                <n-input
                  v-model:value="form.versionNote"
                  type="textarea"
                  placeholder="请输入本次更新日志，日志内容会展示给用户。每日首次发布生效。"
                  :autosize="{ minRows: 4, maxRows: 6 }"
                />
              </n-form-item>

              <div class="publish-inline-fields publish-inline-fields--two">
                <n-form-item label="收费方式">
                  <AppSelectField
                    wide
                    :model-value="form.payType"
                    :options="payTypeOptions"
                    @update:model-value="handlePayTypeUpdate(Number($event))"
                  />
                </n-form-item>
                <n-form-item v-if="form.payType === 1" label="鲜花数">
                  <n-input-number v-model:value="form.flowerUnlock" :min="0" :precision="0" />
                </n-form-item>
              </div>

              <n-form-item label="完结">
                <n-radio-group
                  :value="isFinishedRadioValue"
                  :class="{ 'publish-radio-locked': detailsAllDisabled }"
                  @update:value="handleFinishedUpdate"
                >
                  <n-space>
                    <n-radio :value="1" :disabled="isFirstPublish || detailsAllDisabled">已完结</n-radio>
                    <n-radio :value="0" :disabled="detailsAllDisabled">更新中</n-radio>
                  </n-space>
                </n-radio-group>
              </n-form-item>

              <n-form-item label="所属活动">
                <AppSelectField
                  v-model="form.activityTid"
                  wide
                  :options="activityOptions"
                  :disabled="detailsAllDisabled || detailsActivityDisabled"
                />
              </n-form-item>

              <n-form-item label="小说明星">
                <n-radio-group
                  :value="form.copyrightAction"
                  :class="{ 'publish-radio-locked': detailsAllDisabled || detailsActionDisabled }"
                  @update:value="handleCopyrightActionUpdate"
                >
                  <n-space>
                    <n-radio value="get_partner_list">源自小说</n-radio>
                    <n-radio value="star_copyrigt_company_list">明星授权</n-radio>
                    <n-radio value="0">原创作品</n-radio>
                  </n-space>
                </n-radio-group>
              </n-form-item>

              <div class="publish-inline-fields publish-inline-fields--two">
                <n-form-item label="版权方">
                  <AppSelectField
                    v-model="form.copyrightOwnerId"
                    wide
                    :options="copyrightOwnerOptions"
                    :disabled="detailsAllDisabled || form.copyrightAction === '0'"
                  />
                </n-form-item>

                <n-form-item label="书名">
                  <AppSelectField
                    v-model="form.copyrightWorkId"
                    wide
                    :options="copyrightWorkOptions"
                    :disabled="detailsAllDisabled || form.copyrightAction === '0' || form.copyrightOwnerId === -1000"
                  />
                </n-form-item>
              </div>
            </n-form>
          </n-spin>
        </section>
      </div>
    </section>
  </div>

  <n-modal
    v-model:show="payTypeConfirmOpen"
    preset="card"
    title="提示"
    class="publish-pay-type-modal"
    :mask-closable="false"
  >
    <div class="publish-pay-type-confirm">
      <p>{{ pendingPayTypeMessage }}</p>
      <div class="publish-pay-type-actions">
        <n-button size="small" type="primary" @click="confirmPayTypeUpdate">是</n-button>
        <n-button size="small" @click="cancelPayTypeUpdate">否</n-button>
      </div>
    </div>
  </n-modal>
</template>

<style scoped>
.publish-panel-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
}

.publish-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: min(1080px, 96vw);
  min-height: 70vh;
  max-height: min(900px, calc(100vh - 48px));
  overflow: auto;
  padding: 12px;
  background: var(--theme-bg-surface);
  border: 1px solid var(--theme-border);
}

.publish-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.publish-header > div:first-child {
  min-width: 0;
  flex: 1 1 auto;
}

.publish-header-actions {
  flex: 0 0 auto;
}

.publish-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--theme-text-heading);
}

.publish-subtitle {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  font-size: 12px;
  color: var(--theme-text-secondary);
  writing-mode: horizontal-tb;
  line-height: 1.5;
}

.publish-subtitle span {
  writing-mode: horizontal-tb;
  text-orientation: mixed;
  white-space: normal;
  word-break: break-word;
}

.publish-subtitle-version {
  color: var(--theme-text-very-muted);
}

.publish-progress-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.publish-progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.publish-progress-title {
  min-width: 0;
  color: var(--theme-text-primary);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.publish-progress-title--result {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  line-height: 1.5;
}

.publish-progress-percent {
  flex: none;
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.publish-progress-detail {
  color: var(--theme-text-secondary);
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.publish-result-link {
  width: fit-content;
  color: var(--theme-accent);
  font-size: 12px;
  text-decoration: none;
}

.publish-header-game-link {
  line-height: 28px;
  white-space: nowrap;
}

.publish-result-link:hover {
  color: var(--theme-accent-strong);
}

.publish-post-sync-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.publish-post-sync-item {
  display: grid;
  grid-template-columns: 8px auto minmax(0, 1fr);
  align-items: center;
  gap: 6px;
  color: var(--theme-text-secondary);
  font-size: 12px;
}

.publish-post-sync-dot {
  width: 6px;
  height: 6px;
  background: var(--theme-text-muted);
}

.publish-post-sync-item--running .publish-post-sync-dot {
  background: var(--theme-accent);
}

.publish-post-sync-item--done .publish-post-sync-dot {
  background: var(--theme-accent);
}

.publish-post-sync-item--error .publish-post-sync-dot {
  background: var(--theme-error);
}

.publish-post-sync-label {
  color: var(--theme-text-primary);
}

.publish-post-sync-detail {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.publish-check-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.publish-check-message {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
}

.publish-grid {
  min-height: 0;
}

.publish-tag-web-frame-wrap {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 3000;
  width: min(674px, 96vw);
  transform: translate(-50%, -50%);
  background: #ffffff;
}

.publish-tag-web-error {
  margin-bottom: 8px;
}

.publish-tag-web-frame {
  display: block;
  width: 100%;
  height: min(518px, 92vh);
  border: 0;
  background: #ffffff;
}

.publish-main-card {
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  padding: 12px;
}

.publish-project-name-field {
  width: 100%;
  min-width: 0;
}

.publish-field-hint {
  margin-top: 4px;
  font-size: 12px;
  color: var(--theme-text-muted);
  line-height: 18px;
  white-space: nowrap;
}

.publish-inline-fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.publish-inline-fields--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.publish-radio-locked {
  pointer-events: none;
}

.publish-pay-type-confirm {
  display: grid;
  gap: 16px;
  text-align: center;
}

.publish-pay-type-confirm p {
  margin: 0;
  color: var(--theme-text-secondary);
  line-height: 1.6;
}

.publish-pay-type-actions {
  display: grid;
  grid-template-columns: 120px 120px;
  justify-content: center;
  gap: 12px;
}

:global(body .publish-pay-type-modal.n-modal) {
  width: min(420px, 92vw);
}

@media (max-width: 960px) {
  .publish-panel-overlay {
    padding: 8px;
    box-sizing: border-box;
  }

  .publish-panel {
    width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
    min-height: 0;
  }

  .publish-tag-web-frame-wrap {
    width: calc(100vw - 16px);
  }

  .publish-tag-web-frame {
    height: calc(100vh - 32px);
  }
}

@media (max-width: 960px) and (orientation: portrait) {
  .publish-header {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .publish-header > div:first-child {
    flex: none;
    width: 100%;
  }

  .publish-subtitle {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .publish-header-actions {
    justify-content: flex-end;
  }

  .publish-inline-fields,
  .publish-inline-fields--two {
    grid-template-columns: 1fr;
  }
}
</style>
